import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  Provider,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import {
  AlertComponent,
  CheckboxComponent,
  CheckboxDescription,
  EnumHelper,
  FormGroupComponent,
  FormsUtil,
  ModalService,
  ModalSettings,
  NumberComponent,
  RadioComponent,
  SelectComponent
} from 'mbs-ui-kit';
import { AzureAccessTierType, StorageClass, StorageType, TypesForFFI } from 'mbs-ui-shared@models';
import { PlanMode } from 'mbs-ui-shared@models/PlanTypes.enum';
import { debounceTime } from 'rxjs/operators';
import { AdvancedOptionsStepValue, BlockSizeEnum, FoldersFormData, FoldersFormDataValue } from 'wizards/models/advanced-options-models';
import { ForceValidType } from 'wizards/models/base/other-base-models/force-valid-type';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { FilesFoldersComponent } from 'wizards/steps/components/files-folders/files-folders.component';
import { LegacyPropertyTooltipComponent } from 'wizards/steps/components/legacy-property-tooltip.component';
import { AgentVersions, FormPipeOperators, StepBase } from 'wizards/steps/StepBase.class';
import { AdvancedOptionsStepLogic } from './advanced-options-step.logic';
import { AdvancedOptionsStorageClassAzureTierComponent } from './storage-class-azure-tier/advanced-options-storage-class-azure-tier.component';

const AdvancedOptionsStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => AdvancedOptionsStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-advanced-options-step',
  templateUrl: './advanced-options-step.component.html',
  providers: [AdvancedOptionsStepValueAccessor],
  imports: [
    AlertComponent,
    CheckboxComponent,
    CheckboxDescription,
    CommonModule,
    FilesFoldersComponent,
    FormGroupComponent,
    I18NextPipe,
    LegacyPropertyTooltipComponent,
    NgbTooltipModule,
    NumberComponent,
    RadioComponent,
    ReactiveFormsModule,
    SelectComponent,
    AdvancedOptionsStorageClassAzureTierComponent
  ]
})
export class AdvancedOptionsStepComponent extends StepBase<AdvancedOptionsStepValue> implements OnInit, OnChanges {
  i18nPipe = inject(I18NextPipe);
  mainService: RemoteManagementWizardsService;
  modalService = inject(ModalService);
  private stepService = inject(WizardStepsService);

  @ViewChild('errorSyntheticFull', { static: true, read: TemplateRef }) errorSyntheticFull: TemplateRef<HTMLElement>;
  @ViewChild('fileFoldersComponent', { static: true, read: FilesFoldersComponent }) fileFoldersComponent: FilesFoldersComponent;
  @ViewChild('EFSPayAttention', { static: true, read: TemplateRef }) EFSPayAttention: TemplateRef<HTMLElement>;

  @Input() advancedOptionsFlags = null;

  @Output() changeStorageType = new EventEmitter<{
    azureType?: AzureAccessTierType;
    isStorageBadForFFI: boolean;
    storageClass: StorageClass;
  }>();
  @Output() restoreVerificationEnabled = new EventEmitter();

  public readonly elementSelectors = {
    id: {
      AzureColdWarning: 'advanced-options-azure-cold-warning',
      BackupNTFSPermissions: 'advanced-options-backup-NTFS-permissions-legacy-checkbox',
      BackupNTFSPermissionsLegacyTooltip: 'advanced-options-backup-NTF-permissions-legacy-tooltip',
      SaveDeletedDataInCloud: 'advanced-options-save-deleted-data-in-cloud-checkbox',
      SaveDeletedDataInCloudTooltip: 'advanced-options-save-deleted-data-in-cloud-tooltip',
      SaveDeletedDataInCloudLegacyTooltip: 'advanced-options-save-deleted-data-in-cloud-legacy-tooltip'
    }
  };

  private readonly notSQLDisabledFields = ['UseS3Acceleration', 'StorageClass', 'azureAccessTier'];
  private readonly keepEFSFilesAsIsModalSettings: ModalSettings = {
    header: { title: this.i18nPipe.transform('wizards:keep_efs_encryption_warning_header', { format: 'title' }) },
    footer: {
      okButton: { text: this.i18nPipe.transform('shared:buttons.enable', { format: 'title' }), type: 'primary' },
      cancelButton: { text: this.i18nPipe.transform('shared:buttons.cancel', { format: 'title' }) }
    }
  };

  public advancedOptionsStepLogic: AdvancedOptionsStepLogic;
  public correctlyVersionForShowBlockCount = false;
  public foldersForm: FormGroup<FoldersFormData> = new FormGroup<FoldersFormData>({ filesFoldersData: new FormControl({}) });
  public blockSizesArray = EnumHelper.EnumToSelectIndexesArray(BlockSizeEnum);
  public canShowEFSRequiredInfo = false;
  public saveDeletedDataInCloudChangeHandler = this.getLegacyPropertyChangeHandler(
    'SaveDeletedDataInCloud',
    this.i18nPipe.transform('wizards:mark_deleted_label'),
    this.modalService
  );

  protected legacyUnsupportedProperties = ['SaveDeletedDataInCloud'];

  private needValidate = false;

  /**
   * We can rule this option if:
   * *
   * Files backup
   * NBF and not unix only
   * Agent version >= 7.6
   */
  get isShowEFS(): boolean {
    const rightVersion = this.agentHigherOrEqualThat(AgentVersions.EFS);

    return !this.isUnix && this.isBackupPlan && this.isNBF && (rightVersion || this.isRDMode);
  }

  get isAzureColdSelected(): boolean {
    return this.selectedStorageType === StorageType.Azure && this.stepForm.get('azureAccessTier').value === AzureAccessTierType.Cold;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();
    this.mainService = mainService;

    this.correctlyVersionForShowBlockCount = this.isRDMode || this.agentHigherThat(AgentVersions.BlockCount);
    this.advancedOptionsStepLogic = new AdvancedOptionsStepLogic(this.modalService, this.stepService, this.i18nPipe, this);
  }

  ngOnInit(): void {
    this.initForm();
    this.initEvents();
  }

  protected getPipeOperators(): FormPipeOperators {
    return [untilDestroyed(this), debounceTime(200)];
  }

  initForm(): void {
    this.stepForm = this.advancedOptionsStepLogic.getForm('full');
    this.stepForm.get('SyntheticFull').addValidators(this.syntheticFullValidator.bind(this));

    if (this.isCreate) {
      this.disableLegacyUnsupportedProperties(true);
    }

    this.subscribeToFoldersForm();
    this.initFormEvents();

    setTimeout(() => {
      if (this.mainService.mode === PlanMode.create) return void this.updateFormState(this.advancedOptionsFlags);

      if (this.isSQLPlan) return void this.disableFieldsForSQLPlan(Object.keys(this.stepForm.getRawValue()));
    });

    this.setValues();
  }

  subscribeToFoldersForm(): void {
    this.foldersForm.valueChanges.pipe(untilDestroyed(this)).subscribe((value: FoldersFormDataValue) => {
      if (!value.filesFoldersData) return;

      if (value.filesFoldersData.paths?.length || !value.filesFoldersData.enabledFields) {
        this.needValidate = !this.value.valid;
      }

      this.stepForm.get('ExcludeEnabled').setValue(value.filesFoldersData.enabledFields);
      this.excludeEnabledChangeHandler(value.filesFoldersData.enabledFields);
      this.stepForm.get('excludeRules').setValue(value.filesFoldersData.paths);
    });
  }

  onStepFormChange(value: AdvancedOptionsStepValue): void {
    if (this.stepForm.touched || this.stepForm.dirty) {
      this.value = { ...value, valid: this.stepForm.valid };
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.stepForm && changes.advancedOptionsFlags) {
      this.syntheticFullSettingPossible.next(changes.advancedOptionsFlags.currentValue.SyntheticFullSettingPossible);
      this.updateFormState(changes.advancedOptionsFlags.currentValue);

      if (!this.advancedOptionsFlags.AzureAccessTierType) {
        this.azureAccessTierChangeHandler(AzureAccessTierType.Hot);

        if (this.advancedOptionsFlags.StorageClassSettingPossible) this.storageClassChangeHandler(this.stepForm.get('StorageClass').value);
      }
      if (!this.advancedOptionsFlags.StorageClassSettingPossible) {
        this.storageClassChangeHandler(StorageClass.STANDARD);

        if (this.advancedOptionsFlags.AzureAccessTierType) this.azureAccessTierChangeHandler(this.stepForm.get('azureAccessTier').value);
      }
    }

    if (!this.confirmedAsEnabled && changes?.stepFocused?.currentValue) {
      this.showEnableStepModal(this.modalService);
    }
  }

  syntheticFullValidator(control: AbstractControl): ValidationErrors | null {
    if ((this.isUnix && !this.isNetBuild) || !this.stepForm || !control.value || (!this.isIBBPlan && !this.isNBF)) return null;
    if (!this.advancedOptionsFlags || !this.advancedOptionsStepLogic.needValidateStorageClassOrAzure) return null;

    if (this.advancedOptionsFlags.StorageClassSettingPossible) {
      return TypesForFFI.unsupported.Amazon.includes(this.stepForm.get('StorageClass').value) ? { invalidValue: true } : null;
    }

    if (this.advancedOptionsFlags.AzureAccessTierType || this.isNBF) {
      return TypesForFFI.unsupported.Azure.includes(this.stepForm.get('azureAccessTier').value) ? { invalidValue: true } : null;
    }

    return null;
  }

  updateForm(value: AdvancedOptionsStepValue): void {
    this.initialSyntheticFull = !!value.SyntheticFull; // Should be before change form

    if (this.isRDMode) {
      if (value.ExcludeEnabled || (value.excludeRules && value.excludeRules.length)) {
        delete value.ExcludeEnabled;
        delete value.excludeRules;
        this.importedPlanChanged = true;
      }

      this.correctlyVersionForShowBlockCount = true;
      this.advancedOptionsStepLogic.resetStorageClassesForRD();
    }

    this.stepForm.reset(value);

    this.changeStepFormState(value);

    if (!isNil(value.AlwaysUseVSS)) {
      this.alwaysUseVSSChangeHandler(value.AlwaysUseVSS);

      if (!isNil(value.UseFastNtfsScan)) {
        if (value.UseFastNtfsScan) this.useFastNtfsScanChangeHandler(value.UseFastNtfsScan);
        else this.stepForm.get('AlwaysUseVSS').enable();
      }
    } else if (!isNil(value.UseFastNtfsScan) && !value.UseFastNtfsScan) {
      this.useFastNtfsScanChangeHandler(value.UseFastNtfsScan);
    }

    if (!isNil(value.ExcludeEnabled)) {
      this.excludeEnabledChangeHandler(value.ExcludeEnabled);
      this.foldersForm.get('filesFoldersData').reset({ enabledFields: value.ExcludeEnabled, paths: value.excludeRules });
    }

    this.disableLegacyUnsupportedProperties();
    this.setValuesFromObjOrDefault(value);
    this.updateCanShowEFSRequiredInfo();
  }

  forceValid(currentStep: ForceValidType): void {
    const main = this.stepForm.value;
    this.needValidate = main.ExcludeEnabled && !main.excludeRules.length;

    if (!this.isRDMode && this.advancedOptionsFlags?.ExcludePossible) this.fileFoldersComponent.updateValidate();
    if (
      (!this.isUnix || this.isNetBuild) &&
      (this.isIBBPlan || this.isNBF) &&
      currentStep.isNext &&
      this.stepForm.get('SyntheticFull').invalid
    ) {
      this.showSyntheticFullModal(currentStep.isNext);
    }

    FormsUtil.triggerValidation(this.stepForm);

    if (this.value?.valid && !isNil(this.value?.PrefetchBlockCount)) {
      this.stepForm.get('PrefetchBlockCount').reset(this.value.PrefetchBlockCount);
    }
  }

  storageClassChangeHandler(storageClass: StorageClass): void {
    this.advancedOptionsStepLogic.storageClassChangeHandler(storageClass, false);
  }

  azureAccessTierChangeHandler(type: AzureAccessTierType): void {
    this.advancedOptionsStepLogic.azureAccessTierChangeHandler(type, false);
  }

  syntheticFullChangeHandler(event: boolean): void {
    if (!event) {
      return;
    }

    let specialStorage = false;
    let standardStorageName = '';

    if (this.advancedOptionsFlags?.StorageClassSettingPossible) {
      const selectedClass = this.stepForm.get('StorageClass').value;

      specialStorage = this.advancedOptionsStepLogic.expensiveStorageClasses.S3.includes(selectedClass);
      standardStorageName = String(this.advancedOptionsStepLogic.StorageClasses.find((item) => item.value === StorageClass.STANDARD).label);
    }

    if (this.advancedOptionsFlags?.AzureAccessTierType) {
      const selectedClass = this.stepForm.get('azureAccessTier').value;

      specialStorage = this.advancedOptionsStepLogic.expensiveStorageClasses.Azure.includes(selectedClass);
      standardStorageName = String(this.advancedOptionsStepLogic.AccessTires.find((item) => item.value === AzureAccessTierType.Hot).label);
    }

    if (this.stepFocused && specialStorage) {
      return void AdvancedOptionsStepLogic.showStorageCostImpact(standardStorageName, this.i18nPipe, this.modalService)
        .then((result) => {
          this.stepForm.get('SyntheticFull').setValue(result);
        })
        .catch(() => this.stepForm.get('SyntheticFull').setValue(false));
    }
  }

  useFastNtfsScanChangeHandler(event: boolean): void {
    if (event) {
      this.stepForm.get('AlwaysUseVSS').setValue(true);
      this.alwaysUseVSSChangeHandler(event);
      return void this.stepForm.get('AlwaysUseVSS').disable();
    }

    this.stepForm.get('AlwaysUseVSS').enable();
  }

  alwaysUseVSSChangeHandler(event: boolean): void {
    if (!event) this.stepForm.get('useSystemVSSProvider').setValue(false);

    this.toggleFormControls(['useSystemVSSProvider'], event);
  }

  excludeEnabledChangeHandler(event: boolean): void {
    this.toggleFormControls(['excludeRules'], event);
  }

  keepEFSFilesAsIsChangeHandler(value: boolean): void {
    if (value !== true) return void this.updateCanShowEFSRequiredInfo();

    this.modalService
      .open(this.keepEFSFilesAsIsModalSettings, this.EFSPayAttention)
      .then(() => this.updateCanShowEFSRequiredInfo())
      .catch(() => {
        this.stepForm.get('KeepEFSFilesAsIs').setValue(false);
        this.updateCanShowEFSRequiredInfo();
      });
  }

  // Private block
  private updateFormState(flags): void {
    const keys = Object.keys(flags);
    const formObj = this.stepForm.getRawValue();
    const formObjKeys = Object.keys(formObj);
    const isCreate = this.mainService.mode === PlanMode.create;

    if (this.isSQLPlan) {
      this.disableFieldsForSQLPlan(formObjKeys);
    }

    keys.forEach((key) => {
      const idx = formObjKeys.findIndex((k) => key.toLowerCase().includes(k.toLowerCase()));
      const formKey = formObjKeys[idx];

      if (idx === -1) return;

      if (!this.advancedOptionsFlags[key]) return void this.disableFormFields(formKey);

      this.enableAndSetDefaultValue(
        formKey,
        formKey === 'AlwaysUseVSS'
          ? isNil(this.stepForm.value[formKey]) && !this.stepForm.value['UseFastNtfsScan']
          : (isCreate && formKey === 'SyntheticFull') || (formKey !== 'UseS3Acceleration' && formKey !== 'SyntheticFull')
            ? isNil(this.stepForm.value[formKey])
            : isNil(this.value[formKey])
      );
    });

    this.stepForm.markAsDirty();
    this.stepForm.updateValueAndValidity();
  }

  private initEvents(): void {
    this.advancedOptionsStepLogic.initEvents(this.syntheticFullSettingPossible, false);
  }

  private setValuesFromObjOrDefault(value: AdvancedOptionsStepValue): void {
    this.advancedOptionsStepLogic.setValuesFromObjOrDefault(value, this.isCreate, false);
  }

  private disableFormFields(key): void {
    if (key !== 'SyntheticFull' || (!this.isNBF && !this.isBackupPlan)) this.stepForm.get(key).disable();
  }

  private enableAndSetDefaultValue(key, isNullValue: boolean): void {
    this.advancedOptionsStepLogic.enableAndSetDefaultValue(key, isNullValue, this.isCreate);
  }

  private changeStepFormState(value: AdvancedOptionsStepValue): void {
    /** Don`t disable KeepEFSFilesAsIs radio buttons if not unix */
    const keys = this.isUnix ? Object.keys(value) : Object.keys(value).concat(['KeepEFSFilesAsIs']);
    const formKeys = Object.keys(this.stepForm.getRawValue());

    formKeys.forEach((key) => this.toggleFormControls([key], keys.includes(key)));
  }

  private showSyntheticFullModal(needNextStep: boolean): void {
    this.advancedOptionsStepLogic
      .showSyntheticFullModal(this.errorSyntheticFull)
      .then((confirm) => {
        if (!confirm) return;

        this.stepForm.get('SyntheticFull').setValue(false);
        this.stepForm.updateValueAndValidity();

        if (needNextStep) this.nextStep.emit();
      })
      .catch(() => this.stepForm.updateValueAndValidity());
  }

  private updateCanShowEFSRequiredInfo(): void {
    this.canShowEFSRequiredInfo = !this.isUnix && this.isRDMode && this.stepForm.get('KeepEFSFilesAsIs').value;
  }

  private disableFieldsForSQLPlan(fields: string[]): void {
    fields.forEach((key: string) => {
      if (!this.notSQLDisabledFields.includes(key)) this.stepForm.get(key).disable();
    });
  }
}
